'use client'
import { Button } from '@/components/Button'
import { Loading } from '@/components/Loading'
import { Popup } from '@/components/Popup'
import { Icon } from '@/icons'
import { MktuClassStore } from '@/store/Mktu/MktuClass'
import { SearchMktuClassStore } from '@/store/Mktu/SearchMktuClass'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { FunctionComponent, useCallback } from 'react'
import { Card } from './components/Card'
import styles from './MktuClassListPopup.module.scss'

interface MktuClassListPopupProps {
  store: SearchMktuClassStore
  onClose: () => void
  onCancel: () => void
  onApply?: () => void
  showSelectWordsPopup: (mktuClass: MktuClassStore) => void
  selectedClasses: MktuClassStore[]
  className?: string
}
export const MktuClassListPopup: FunctionComponent<MktuClassListPopupProps> =
  observer(
    ({
      store,
      onClose,
      onCancel,
      onApply,
      showSelectWordsPopup,
      selectedClasses,
      className,
    }) => {
      const selectWords = useCallback(
        (mktuClassId: MktuClassStore['uuid']) => {
          if (typeof showSelectWordsPopup === 'function') {
            const selectedMktuClass = store.foundClasses.find(
              (c) => c.uuid === mktuClassId
            )
            if (selectedMktuClass != null) {
              showSelectWordsPopup(selectedMktuClass)
            }
          }
        },
        [store, showSelectWordsPopup]
      )

      const apply = useCallback(() => {
        if (typeof onApply === 'function') {
          onApply()
        }
      }, [onApply])

      return (
        <Popup
          onClose={onClose}
          className={classNames(className, styles.popup)}
        >
          <div className={styles.popupContainer}>
            {!store.loadingClasses ? (
              <div className={styles.header}>
                <button
                  type="button"
                  onClick={onCancel}
                  className={styles.backButton}
                >
                  <Icon name="back" className={styles.iconBack} /> Назад
                </button>
                <div className={styles.title}>
                  Подобрано классов: {store.foundClasses.length}
                </div>
              </div>
            ) : null}
            <div className={styles.body}>
              {store.loadingClasses ? (
                <Loading withText={false} />
              ) : (
                <div className={styles.content}>
                  <button onClick={onClose} className={styles.closeButton}>
                    <Icon name="close" className={styles.closeButtonIcon} />
                  </button>
                  <div className={styles.list}>
                    {store.foundClasses.map((mktuClass) => (
                      <Card
                        key={mktuClass.uuid}
                        mktuClass={mktuClass}
                        onEdit={selectWords}
                      />
                    ))}
                  </div>
                  {store.notFoundWords.length !== 0 ? (
                    <div className={styles.notFoundWordsList}>
                      Не найдено результатов по запросам{' '}
                      <span className={styles.notFoundWord}>
                        {store.notFoundWords.map((w) => `«${w}»`).join(', ')}
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            {!store.loadingClasses ? (
              <div className={styles.footer}>
                {selectedClasses.length !== 0 ? (
                  <div className={styles.resultText}>
                    <span className={styles.resultTextLabel}>
                      Будут применены позиции следующих классов МКТУ:
                    </span>{' '}
                    {selectedClasses.map((c) => c.number).join(', ')}
                  </div>
                ) : null}
                <div className={styles.footerRight}>
                  <Button
                    className={styles.button}
                    name="Подтвердить"
                    onClick={apply}
                    disabled={store.foundClasses.length === 0}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </Popup>
      )
    }
  )
